import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, WhatsappLogo, LinkedinLogo, XLogo, InstagramLogo, ThreadsLogo, FacebookLogo, EnvelopeSimple, Link as LinkIcon, TextAa, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import BottomSheet from "@/components/BottomSheet";
import api from "@/lib/api";

/**
 * ShareJobModal — one-tap share widget that spreads a JD across
 * WhatsApp, LinkedIn, Twitter/X, and email. Every button uses the
 * server-rendered `/api/share/j/{id}` URL so that WhatsApp / LinkedIn
 * crawlers get proper OG previews (see backend server.py::share_job).
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - jobId: string
 *   - jobTitle: string        (used only for the pre-filled message body)
 *   - clientName?: string     (optional — added to WhatsApp / Twitter copy)
 *   - refCode?: string        (candidate referral code — appended as ?ref= to
 *                               track bounty attribution. Omit for recruiter
 *                               / anonymous shares.)
 *   - salary?: string         (optional — added to preview copy)
 *   - location?: string       (optional — added to preview copy)
 */
export default function ShareJobModal({ open, onClose, jobId, jobTitle, clientName, refCode, salary, location }) {
    const [copied, setCopied] = useState(false);
    const [textCopied, setTextCopied] = useState(false);
    // V2.0.49 (Feb 26, 2026) — live scraped-preview state. `scraped`
    // holds the actual card the FB/LinkedIn/WhatsApp bots would fetch.
    // Kicked off automatically when the modal opens; recruiter can hit
    // "Refresh" to re-scrape after they push new share text or JD copy.
    const [scraped, setScraped] = useState(null);
    const [scraping, setScraping] = useState(false);
    const [scrapeError, setScrapeError] = useState(null);
    // V2.0.50 (Feb 26, 2026) — Custom share headline. Recruiters can
    // add a per-share hook line ("🔥 Weekend hiring drive — 3 seats")
    // that's prepended to the message they copy/share. Deliberately
    // does NOT touch the OG image or JD — only the caption text.
    // Persisted per-job in localStorage so a saved tagline sticks
    // across sessions and devices for the same recruiter's browser.
    const HEADLINE_MAX = 120;
    const headlineKey = jobId ? `share:tagline:${jobId}` : null;
    const [headline, setHeadline] = useState("");
    useEffect(() => {
        if (!open || !headlineKey) return;
        try {
            const v = window.localStorage.getItem(headlineKey);
            setHeadline(v || "");
        } catch { setHeadline(""); }
    }, [open, headlineKey]);
    const saveHeadline = (v) => {
        setHeadline(v);
        if (!headlineKey) return;
        try {
            if (v) window.localStorage.setItem(headlineKey, v);
            else window.localStorage.removeItem(headlineKey);
        } catch { /* private mode / quota — silent */ }
    };
    // Share the clean public job URL — LinkedIn/WhatsApp/Twitter all crawl the
    // OG tags from `/jobs/{id}` (which, when visited by a bot, redirects to
    // the server-rendered OG page). Sharing `/api/share/j/{id}` directly was
    // blocked by LinkedIn with ERR_BLOCKED_BY_RESPONSE because the JS-redirect
    // HTML confuses its inline preview.
    // V1.5.8.56 — force each shared URL to be UNIQUE per job so LinkedIn /
    // Facebook / X can't dedup them to the homepage. Every route in our
    // React SPA returns identical shell HTML today; without a differentiator
    // in the URL the social canonicalizer collapses `/jobs/{id}` → `/`.
    // A short `?j={id}` param is enough to break the dedup. It's also
    // easier to eyeball in Search Console / share logs than a hash.
    const baseUrl = `${window.location.origin}/jobs/${jobId}`;
    const query = new URLSearchParams();
    if (refCode) query.set("ref", refCode);
    query.set("j", jobId);  // dedup-breaker
    const shareUrl = `${baseUrl}?${query.toString()}`;

    // V2.0.59 — Share Analytics funnel. Every modal-open mints a fresh
    // `shareId` (crypto.randomUUID) so tap → land → apply can be stitched
    // by the backend into per-channel/per-headline conversion.
    // `buildShareUrlFor(channel)` appends utm_source={channel}, utm_medium=share,
    // utm_campaign=job_share, and share_id — the SPA reads them on landing
    // and fires the `land` event.
    const shareIdRef = useRef("");
    useEffect(() => {
        if (!open) return;
        try {
            shareIdRef.current =
                (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID().replace(/-/g, "").slice(0, 20))
                || Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
        } catch { shareIdRef.current = Math.random().toString(36).slice(2, 14); }
    }, [open]);

    const buildShareUrlFor = (channel) => {
        const q = new URLSearchParams();
        if (refCode) q.set("ref", refCode);
        q.set("j", jobId);
        if (channel) {
            q.set("utm_source", channel);
            q.set("utm_medium", "share");
            q.set("utm_campaign", "job_share");
        }
        if (shareIdRef.current) q.set("share_id", shareIdRef.current);
        return `${baseUrl}?${q.toString()}`;
    };

    // Fire-and-forget tap event. Never blocks the actual share action —
    // clipboard/native-share is what the user paid attention to.
    const fireTap = (channel) => {
        try {
            api.post("/share/events", {
                event: "tap",
                job_id: jobId,
                channel,
                share_id: shareIdRef.current,
                ref_code: refCode || null,
                has_custom_headline: !!headline.trim(),
                headline_len: (headline || "").trim().length,
            }).catch(() => {}); // silent — analytics never breaks share UX
        } catch { /* ignore */ }
    };
    const heading = clientName ? `${jobTitle} at ${clientName}` : jobTitle;
    const details = [salary, location].filter(Boolean).join(" · ");

    // V1.5.8.57 — auto-generate hashtags from job data. Cleaned + capped
    // at 5 to keep LinkedIn feed algorithms happy. Location + first
    // significant word of the title + evergreen tags.
    const toTag = (s) => (s || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 25);
    const titleWords = (jobTitle || "").split(/\s+/).filter((w) => w.length >= 4);
    const hashtagsList = [
        "Hiring",
        titleWords[0] ? toTag(titleWords[0]) : "JobOpening",
        toTag((location || "").split(",")[0]) || "India",
        "NaukriDaddy",
        refCode ? "RefAndEarn" : "JobSearch",
    ].filter(Boolean).filter((t, i, a) => t && a.indexOf(t) === i).slice(0, 5);
    const hashtags = hashtagsList.map((t) => `#${t}`).join(" ");

    // Rich message = emoji + heading + facets + CTA + hashtags — used
    // as the WhatsApp/X/Threads pre-fill AND copied to clipboard when
    // the user clicks LinkedIn/Facebook (whose share URLs don't accept
    // pre-filled text).
    // V2.0.50 — optional custom headline is prepended when set.
    const headlinePrefix = headline.trim() ? `${headline.trim()}\n\n` : "";
    const message = headlinePrefix + (details
        ? `👀 ${heading}\n📍 ${details}\n\nApply directly: ${shareUrl}${refCode ? "\n(via NaukriDaddy)" : ""}\n\n${hashtags}`
        : `${heading} — apply on NaukriDaddy: ${shareUrl}\n\n${hashtags}`);

    // Helper the click handlers use to prime the clipboard before opening
    // LinkedIn/Facebook so the user can paste in one tap.
    const copyMessageToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(message);
            toast.success("Post text + hashtags copied — paste with Cmd/Ctrl+V");
        } catch (_) {
            /* silent — the share URL still opens with the link */
        }
    };

    useEffect(() => {
        if (!open) { setCopied(false); setTextCopied(false); }
    }, [open]);

    // V2.0.49 — refetch the live scraped card whenever the modal opens
    // or the shareUrl changes (different job selected in a bulk flow).
    const loadScrape = useCallback(async () => {
        if (!shareUrl) return;
        setScraping(true);
        setScrapeError(null);
        try {
            const r = await api.get("/share/preview", { params: { url: shareUrl } });
            setScraped(r.data);
            if (!r.data?.ok) {
                setScrapeError(r.data?.error || "Couldn't scrape the shared URL.");
            }
        } catch (e) {
            setScrapeError(e?.response?.data?.detail || "Live preview unavailable.");
            setScraped(null);
        } finally { setScraping(false); }
    }, [shareUrl]);

    useEffect(() => {
        if (open) { loadScrape(); }
        else { setScraped(null); setScrapeError(null); }
    }, [open, loadScrape]);

    if (!open) return null;

    const copy = async () => {
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(shareUrl);
                ok = true;
            }
        } catch (_) { /* fall through */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = shareUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (_) { /* ignore */ }
        }
        if (ok) {
            // V2.0.59 — count "Copy link" as a share too so the analytics
            // funnel isn't blind to the recruiter's most-used channel.
            fireTap("copy_link");
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    // V1.5.9 — Explicit "Copy post text" flow. LinkedIn / Facebook /
    // Instagram all strip caption params from their share intents, so
    // we surface the exact hashtag-rich text in a visible textarea +
    // give the user one obvious button to copy it. Even if the auto-
    // clipboard on LinkedIn/FB click silently fails (permission /
    // iframe / focus loss), this button always works.
    const copyPostText = async () => {
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(message);
                ok = true;
            }
        } catch (_) { /* fall through */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = message;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (_) { /* ignore */ }
        }
        if (ok) {
            // V2.0.59 — copy_text is another share signal for analytics.
            fireTap("copy_text");
            setTextCopied(true);
            setTimeout(() => setTextCopied(false), 1800);
            toast.success("Post text + hashtags copied — paste with Cmd/Ctrl+V");
        } else {
            toast.error("Couldn't copy — long-press the text and copy manually.");
        }
    };

    // Try to open in a new tab; if the parent iframe or popup blocker
    // suppresses window.open we fall back to navigating the top-level
    // browsing context so the click at least *goes somewhere*.
    //
    // NOTE: window.open(url, "_blank", "noopener") *always* returns null
    // in modern browsers because noopener detaches the reference — we
    // must NOT treat null as "popup blocked" or we'd wrongly navigate
    // the current tab too. Only fall back when we're actually inside an
    // iframe (preview sandbox), where popups can genuinely be blocked.
    const navigate = (url) => {
        const inIframe = (() => {
            try { return window.top !== window.self; } catch (_) { return true; }
        })();
        try {
            const w = window.open(url, "_blank", "noopener,noreferrer");
            // If not in an iframe, always trust window.open. Even if `w`
            // is null (noopener flag) the tab opened correctly.
            if (!inIframe) return;
            if (w) return;
        } catch (_) { /* fall through */ }
        try {
            // Escape iframe sandboxes that block popups
            window.top.location.href = url;
        } catch (_) {
            window.location.href = url;
        }
    };

    const links = [
        {
            key: "whatsapp",
            label: "WhatsApp",
            href: `https://wa.me/?text=${encodeURIComponent(headlinePrefix + (details
                ? `👀 ${heading}\n📍 ${details}\n\nApply directly: ${buildShareUrlFor("whatsapp")}${refCode ? "\n(via NaukriDaddy)" : ""}\n\n${hashtags}`
                : `${heading} — apply on NaukriDaddy: ${buildShareUrlFor("whatsapp")}\n\n${hashtags}`))}`,
            Icon: WhatsappLogo,
            color: "bg-[#25D366] hover:bg-[#1ebe5a] text-white",
        },
        {
            key: "linkedin",
            label: "LinkedIn",
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildShareUrlFor("linkedin"))}`,
            Icon: LinkedinLogo,
            color: "bg-[#0A66C2] hover:bg-[#084b91] text-white",
            beforeOpen: copyMessageToClipboard,   // LinkedIn ignores text params
        },
        {
            key: "facebook",
            label: "Facebook",
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildShareUrlFor("facebook"))}`,
            Icon: FacebookLogo,
            color: "bg-[#1877F2] hover:bg-[#0e5fc1] text-white",
            beforeOpen: copyMessageToClipboard,   // FB also ignores text params
        },
        {
            key: "twitter",
            label: "X (Twitter)",
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(headlinePrefix + (details
                ? `👀 ${heading}\n📍 ${details}\n\nApply directly: ${buildShareUrlFor("twitter")}${refCode ? "\n(via NaukriDaddy)" : ""}\n\n${hashtags}`
                : `${heading} — apply on NaukriDaddy: ${buildShareUrlFor("twitter")}\n\n${hashtags}`))}`,
            Icon: XLogo,
            color: "bg-black hover:bg-[#222] text-white",
        },
        {
            // Threads (by Meta) — full text pre-fill via its intent endpoint.
            // Threads posts cap at 500 chars, well above our 200-char message.
            key: "threads",
            label: "Threads",
            href: `https://www.threads.net/intent/post?text=${encodeURIComponent(headlinePrefix + (details
                ? `👀 ${heading}\n📍 ${details}\n\nApply directly: ${buildShareUrlFor("threads")}${refCode ? "\n(via NaukriDaddy)" : ""}\n\n${hashtags}`
                : `${heading} — apply on NaukriDaddy: ${buildShareUrlFor("threads")}\n\n${hashtags}`))}`,
            Icon: ThreadsLogo,
            color: "bg-[#0A0A0A] hover:bg-[#222] text-white",
        },
        {
            key: "instagram",
            label: "Instagram",
            // Instagram has no public share-intent URL. Copy the caption to
            // clipboard AND open instagram.com so the user has somewhere to
            // paste — same navigate() fallback handles iframe popup blocks.
            action: async () => {
                let ok = false;
                try {
                    if (navigator.clipboard && window.isSecureContext) {
                        await navigator.clipboard.writeText(message);
                        ok = true;
                    }
                } catch (_) { /* fall through */ }
                if (!ok) {
                    try {
                        const ta = document.createElement("textarea");
                        ta.value = message;
                        ta.style.position = "fixed";
                        ta.style.opacity = "0";
                        document.body.appendChild(ta);
                        ta.focus();
                        ta.select();
                        ok = document.execCommand("copy");
                        document.body.removeChild(ta);
                    } catch (_) { /* ignore */ }
                }
                toast.success(
                    ok
                        ? "Caption copied. Open Instagram and paste it into your Story, Bio, or DM."
                        : "Long-press the link below to copy manually, then open Instagram.",
                    { duration: 5500 },
                );
            },
            Icon: InstagramLogo,
            color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white",
        },
        {
            key: "email",
            label: "Email",
            href: `mailto:?subject=${encodeURIComponent(`Open role: ${heading}`)}&body=${encodeURIComponent(message)}`,
            Icon: EnvelopeSimple,
            color: "bg-white border border-[#E5E5E5] hover:border-ink text-ink",
        },
    ];

    return (
        <BottomSheet
            open={true}
            onClose={onClose}
            testId="share-job-modal"
            title={
                <span className="min-w-0 flex flex-col">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3]">
                        Share this role
                    </span>
                    <span className="font-heading text-base font-bold tracking-tight text-ink line-clamp-1">
                        {heading}
                    </span>
                </span>
            }
        >

                <div className="px-5 py-5 space-y-4">
                    {/* V2.0.49 (Feb 26, 2026) — LIVE scraped preview.
                        Replaces the earlier mocked card. The backend
                        `/api/share/preview?url=...` fetches the shared
                        URL with a Facebook bot UA and parses `og:*`
                        tags, so what the recruiter sees == exactly what
                        Facebook / LinkedIn / WhatsApp will render. The
                        Refresh button re-scrapes after JD copy edits or
                        cache warmups. */}
                    <div data-testid="share-og-preview" className="border border-[#E5E5E5] bg-[#F5F5F5]">
                        <div className="bg-white px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between gap-2">
                            <span className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                How it looks when shared
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                                <span
                                    data-testid="share-og-preview-source"
                                    className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                                        scraping
                                            ? "bg-slate-100 text-slate-500"
                                            : scraped?.ok
                                                ? "bg-emerald-100 text-emerald-700"
                                                : "bg-amber-100 text-amber-700"
                                    }`}
                                    title={
                                        scraped?.rendered_by
                                            ? `Rendered by ${scraped.rendered_by}`
                                            : "Live-scraped as facebookexternalhit"
                                    }
                                >
                                    {scraping ? "Scraping…" : scraped?.ok ? "Live" : "Fallback"}
                                </span>
                                <button
                                    type="button"
                                    onClick={loadScrape}
                                    disabled={scraping}
                                    data-testid="share-og-preview-refresh"
                                    className="text-[10px] font-black uppercase tracking-wider text-[#525252] hover:text-ink disabled:opacity-40 inline-flex items-center gap-1"
                                    title="Re-scrape the shared URL as a Facebook bot would"
                                >
                                    <ArrowsClockwise size={11} weight="bold" className={scraping ? "animate-spin" : ""} />
                                    Refresh
                                </button>
                            </div>
                        </div>
                        <div className="p-3">
                            {(() => {
                                // Prefer live scrape; fall back to locally-computed values
                                // if the scrape fails so the modal is never empty.
                                const card = scraped?.card || {};
                                const previewTitle = card.title || heading;
                                const previewDesc = card.description
                                    || `${details ? details + ". " : ""}Apply directly on NaukriDaddy — no candidate fees.`;
                                const previewImage = card.image
                                    || `${window.location.origin}/api/seo/og/job/${jobId}.png?v=20260220b`;
                                const previewSite = (card.site_name || "naukridaddy.com").toLowerCase();
                                return (
                                    <div className="bg-white border border-[#E5E5E5] overflow-hidden max-w-[420px] mx-auto">
                                        <div className="aspect-[1200/630] bg-ink overflow-hidden">
                                            <img
                                                src={previewImage}
                                                alt={`Share preview for ${previewTitle}`}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    if (!e.currentTarget.src.endsWith("/og-default.png?v=20260220b")) {
                                                        e.currentTarget.src = `${window.location.origin}/og-default.png?v=20260220b`;
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="px-3.5 py-3 border-t border-[#F5F5F5]">
                                            <p
                                                data-testid="share-og-preview-title"
                                                className="text-[13px] font-black text-ink line-clamp-2 leading-snug"
                                            >
                                                {previewTitle}
                                            </p>
                                            <p
                                                data-testid="share-og-preview-desc"
                                                className="text-[11.5px] text-[#525252] leading-relaxed mt-1 line-clamp-2"
                                            >
                                                {previewDesc}
                                            </p>
                                            <p className="text-[10px] text-[#A3A3A3] mt-1.5 tracking-wide uppercase">
                                                {previewSite}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}
                            {scrapeError && (
                                <div
                                    data-testid="share-og-preview-error"
                                    className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 px-2 py-1.5"
                                >
                                    <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
                                    <span>
                                        Couldn't fetch the live card — showing a local fallback.
                                        ({scrapeError})
                                    </span>
                                </div>
                            )}
                            <p className="text-[10px] text-[#A3A3A3] text-center mt-3">
                                {scraped?.ok
                                    ? "Live-scraped as facebookexternalhit — this is exactly what recipients will see on WhatsApp, LinkedIn, Facebook, X & Threads."
                                    : "This is a local preview. WhatsApp, LinkedIn, Facebook, X & Threads will scrape the same tags at share time."}
                            </p>
                        </div>
                    </div>

                    {/* V1.5.9 — Visible "post text" panel. LinkedIn / Facebook /
                        Instagram strip caption params from share intents, so
                        the user needs the exact hashtag-rich text in front of
                        them with one obvious button to copy it. */}
                    {/* V2.0.50 — Custom headline. Recruiters can prepend
                        a per-share hook ("🔥 Weekend hiring drive — 3 seats")
                        that gets added to the caption on every platform
                        WITHOUT touching the JD or the OG image. Persisted
                        per-job in localStorage. */}
                    <div data-testid="share-headline-panel" className="border border-[#E5E5E5]">
                        <div className="flex items-center justify-between px-3 py-2 bg-[#F5F5F5] border-b border-[#E5E5E5]">
                            <div className="flex items-center gap-1.5">
                                <TextAa size={12} weight="bold" className="text-[#525252]" />
                                <span className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    Your headline (optional)
                                </span>
                            </div>
                            <span
                                data-testid="share-headline-count"
                                className={`text-[9px] font-black uppercase tracking-wider ${
                                    headline.length > HEADLINE_MAX
                                        ? "text-red-600"
                                        : "text-[#A3A3A3]"
                                }`}
                            >
                                {headline.length}/{HEADLINE_MAX}
                            </span>
                        </div>
                        <textarea
                            value={headline}
                            onChange={(e) => saveHeadline(e.target.value.slice(0, HEADLINE_MAX + 20))}
                            data-testid="share-headline-input"
                            placeholder="🔥 Weekend hiring drive — 3 seats · Immediate joiners"
                            rows={2}
                            className="w-full px-3 py-2 text-xs text-ink leading-relaxed resize-none outline-none bg-white placeholder:text-[#A3A3A3]"
                        />
                        <div className="text-[10px] text-[#525252] px-3 py-2 border-t border-[#F5F5F5] bg-[#FAFAFA] leading-relaxed flex items-start justify-between gap-2">
                            <span>
                                Prepended to your post caption on every platform. Doesn't change the JD or share card image.
                            </span>
                            {headline && (
                                <button
                                    type="button"
                                    onClick={() => saveHeadline("")}
                                    data-testid="share-headline-clear"
                                    className="shrink-0 text-[10px] font-black uppercase tracking-wider text-[#525252] hover:text-ink"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <div data-testid="share-post-text-panel" className="border border-[#E5E5E5]">
                        <div className="flex items-center justify-between px-3 py-2 bg-[#F5F5F5] border-b border-[#E5E5E5]">
                            <div className="flex items-center gap-1.5">
                                <TextAa size={12} weight="bold" className="text-[#525252]" />
                                <span className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                    Your post text
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={copyPostText}
                                data-testid="share-copy-post-text"
                                className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                                    textCopied
                                        ? "bg-emerald-500 text-white"
                                        : "bg-ink hover:bg-black text-white"
                                }`}
                            >
                                {textCopied ? (
                                    <><Check size={11} weight="bold" /> Copied</>
                                ) : (
                                    <><Copy size={11} weight="bold" /> Copy post text</>
                                )}
                            </button>
                        </div>
                        <textarea
                            readOnly
                            value={message}
                            data-testid="share-post-text-body"
                            onFocus={(e) => e.target.select()}
                            rows={5}
                            className="w-full px-3 py-2 text-xs text-ink font-mono leading-relaxed resize-none outline-none bg-white"
                        />
                        <p className="text-[10px] text-[#525252] px-3 py-2 border-t border-[#F5F5F5] bg-[#FAFAFA] leading-relaxed">
                            <strong>Tip:</strong> LinkedIn &amp; Facebook don't allow pre-filled captions.
                            Tap <strong>Copy post text</strong> above, click LinkedIn/Facebook below, then paste (Cmd/Ctrl+V) into their composer.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        {links.map(({ key, label, href, action, Icon, color, beforeOpen }) => {
                            const inner = (
                                <>
                                    <Icon size={16} weight="bold" />
                                    {label}
                                </>
                            );
                            const cls = `${color} px-3 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors`;
                            if (action) {
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => { fireTap(key); action(); }}
                                        data-testid={`share-job-${key}`}
                                        className={cls}
                                    >
                                        {inner}
                                    </button>
                                );
                            }
                            // Anchor + explicit navigate() fallback: `target=_blank`
                            // gets silently blocked inside preview iframes without
                            // `allow-popups`; navigate() escapes to window.top when
                            // the popup call returns null.
                            return (
                                <a
                                    key={key}
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid={`share-job-${key}`}
                                    className={cls}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        // V2.0.59 — record the tap BEFORE opening the
                                        // target so we survive the navigation. api.post
                                        // is fire-and-forget so it doesn't add latency.
                                        fireTap(key);
                                        // V1.5.8.57 — LinkedIn / Facebook silently
                                        // ignore ?text= / ?summary= params, so we
                                        // pre-copy the summary + hashtags to the
                                        // clipboard first, then open the share URL.
                                        // User pastes with one Cmd/Ctrl+V.
                                        if (beforeOpen) { beforeOpen(); }
                                        navigate(href);
                                    }}
                                >
                                    {inner}
                                </a>
                            );
                        })}
                    </div>

                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1.5">
                            Share link
                        </p>
                        <div className="flex items-stretch">
                            <div className="flex-1 min-w-0 border border-[#E5E5E5] border-r-0 px-3 py-2.5 text-xs text-[#525252] font-mono truncate flex items-center gap-2">
                                <LinkIcon size={12} weight="bold" className="text-[#A3A3A3] shrink-0" />
                                <span className="truncate" data-testid="share-job-url">{shareUrl}</span>
                            </div>
                            <button
                                onClick={copy}
                                data-testid="share-job-copy"
                                className={`px-4 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                                    copied
                                        ? "bg-emerald-500 text-white"
                                        : "bg-ink hover:bg-black text-white"
                                }`}
                            >
                                {copied ? (
                                    <><Check size={12} weight="bold" /> Copied</>
                                ) : (
                                    <><Copy size={12} weight="bold" /> Copy</>
                                )}
                            </button>
                        </div>
                        <p className="text-[11px] text-[#A3A3A3] mt-2">
                            This link opens on WhatsApp, LinkedIn &amp; email with a
                            rich preview card (title, description, logo).
                        </p>
                    </div>
                </div>
        </BottomSheet>
    );
}
